import { forwardRef, useEffect, useMemo } from "react";
import { useMatch } from "react-router-dom";
import { createListCollection, useSelectContext } from "@ark-ui/react";

import { useTranslation } from "@/lib/i18n";
import { usePublishedLocationsQuery } from "@/features/dashboard/features/dashboard";
import {
  useDashboardStoreActions,
  useSelectedLocationId,
} from "@/features/dashboard/store";
import Select from "@/components/select/select";
import Button from "@/components/button/button";
import { Icon } from "@/components/icon/icon";

import styles from "./location-selector.module.css";

interface LocationSelectorProps {
  dashboardId: string;
}

/**
 * Toolbar location control. The toolbar button *is* the dropdown trigger: there
 * is no intermediate panel. Selecting a location closes the list automatically
 * (Ark `Select` default) and updates the shared runtime.
 */
export default function LocationSelector({ dashboardId }: LocationSelectorProps) {
  const { t } = useTranslation();
  const locationsQuery = usePublishedLocationsQuery(dashboardId);
  const { setSelectedLocationId } = useDashboardStoreActions();
  const selectedLocationId = useSelectedLocationId();
  const { params } = useMatch("/:dashboard/*") ?? {};

  const locationOptions = useMemo(
    () =>
      locationsQuery.data?.map((location) => ({
        label: location.name,
        value: location.id,
      })) ?? [],
    [locationsQuery.data],
  );

  const selectedLocation = locationsQuery.data?.find(
    (location) => location.id === selectedLocationId,
  );

  const isSelectedLocationInLocations = useMemo(
    () => locationsQuery.data?.some((location) => location.id === selectedLocationId),
    [locationsQuery.data, selectedLocationId],
  );

  // Default to the first published location when the current selection is not
  // part of this dashboard's set (e.g. after switching dashboards). The control
  // is always mounted in the toolbar, so this now runs eagerly rather than only
  // while a picker panel is open.
  useEffect(() => {
    if (!isSelectedLocationInLocations && locationsQuery.data?.length) {
      setSelectedLocationId(locationsQuery.data[0]?.id);
    }
  }, [
    params?.dashboard,
    locationsQuery.data,
    isSelectedLocationInLocations,
    setSelectedLocationId,
  ]);

  const locationCollection = useMemo(
    () => createListCollection({ items: locationOptions }),
    [locationOptions],
  );

  if (!locationsQuery.isSuccess || locationsQuery.data.length === 0) return null;

  return (
    <Select
      className={styles.locationSelect}
      collection={locationCollection}
      value={selectedLocationId}
      onValueChange={setSelectedLocationId}
      positioning={{ sameWidth: false }}
      trigger={
        <LocationSelectTrigger label={selectedLocation?.name || t("common.location")} />
      }
    />
  );
}

const LocationSelectTrigger = forwardRef<
  HTMLButtonElement,
  { label: string } & React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ label, ...rest }, ref) => {
  const context = useSelectContext();

  return (
    <Button ref={ref} variant="primary" {...rest}>
      <Icon name="locate" size={16} />
      <span>{label}</span>
      <Icon name={context.open ? "chevron-up" : "chevron-down"} size={16} />
    </Button>
  );
});
LocationSelectTrigger.displayName = "LocationSelectTrigger";
